import { type FC, type ReactNode, useState, useSyncExternalStore } from 'react';

import {
  Badge,
  Button,
  Checkbox,
  Heading,
  Icon,
  Text,
  toast,
} from '@eluve/components';
import { useIsPostHogFeatureFlagEnabled } from '@eluve/posthog-react';
import { useIsFeatureFlagEnabled } from '@eluve/smart-blocks';

import type { MiniAppPanelProps } from '../../types';

import { EditClinicalTaskSheet } from './EditClinicalTaskSheet';
import {
  CLINICAL_TASK_PRIORITY_CLASSES,
  CLINICAL_TASK_PRIORITY_LABELS,
  asPriority,
  categoryIcon,
  categoryLabel,
} from './constants';
import {
  getTasksPanelSnapshot,
  subscribeTasksPanel,
} from './tasks-panel-store';
import {
  type PendingClinicalTask,
  useClinicalTasksPanelState,
} from './useClinicalTasksPanelState';

/** One "label: value" line on the card; renders nothing without a value. */
const DetailRow: FC<{ label: string; value: ReactNode }> = ({
  label,
  value,
}) =>
  value ? (
    <div className="flex items-baseline gap-3">
      <Text size="sm" color="tertiary" className="w-24 shrink-0">
        {label}
      </Text>
      <Text size="sm" weight="medium">
        {value}
      </Text>
    </div>
  ) : null;

/** Prototype-styled priority chip (P1 · Highest … P4 · Low), or nothing. */
const PriorityChip: FC<{ priority: string | null }> = ({ priority }) => {
  const known = asPriority(priority);
  if (!known) return null;
  return (
    <span
      className={`inline-flex w-fit items-center rounded-md px-2 py-0.5 text-xs font-semibold ${CLINICAL_TASK_PRIORITY_CLASSES[known]}`}
      data-testid="clinical-task-priority"
    >
      {CLINICAL_TASK_PRIORITY_LABELS[known]}
    </span>
  );
};

/**
 * The Clinical Tasks inline panel (ENG-5223): AI-suggested follow-up actions
 * awaiting review, then the accepted set tracked Todo → Done. Renders nothing
 * unless the tenant CLINICAL_TASKS flag AND the same-named PostHog user flag
 * are both on (extraction keeps running server-side regardless), or when
 * there is nothing at all to show.
 */
export const ClinicalTasksPanel: FC<MiniAppPanelProps> = ({ context }) => {
  const tenantFlagOn = useIsFeatureFlagEnabled('CLINICAL_TASKS');
  const userFlagOn = useIsPostHogFeatureFlagEnabled('CLINICAL_TASKS');

  if (!tenantFlagOn || !userFlagOn) return null;
  return <ClinicalTasksPanelContent context={context} />;
};

const ClinicalTasksPanelContent: FC<MiniAppPanelProps> = ({ context }) => {
  const { tenantId, appointmentId } = context;
  const state = useClinicalTasksPanelState({ tenantId, appointmentId });
  const [editing, setEditing] = useState<PendingClinicalTask | null>(null);
  // The Tasks action button toggles this; the hook above keeps polling while
  // collapsed so the button's count stays live.
  const { collapsed } = useSyncExternalStore(
    subscribeTasksPanel,
    getTasksPanelSnapshot,
  );

  const decide = async (id: string, decision: 'approve' | 'reject') => {
    const ok = await state.decide(id, decision);
    if (ok) {
      toast.success(
        decision === 'approve' ? 'Task accepted.' : 'Task rejected.',
      );
    } else {
      toast.error('Could not submit the decision.');
    }
  };

  const setDone = async (
    task: (typeof state.accepted)[number],
    done: boolean,
  ) => {
    const ok = await state.setDone(task, done);
    if (!ok) toast.error('Could not update the task.');
  };

  // Nothing extracted / accepted (or still loading), or hidden via the Tasks
  // button → the SECTION contributes no DOM so the summary keeps its exact
  // layout. The edit sheet renders OUTSIDE that gate: it's a portal overlay,
  // and an open editor must survive panel re-loads/collapses — unmounting it
  // mid-edit silently discards the user's un-debounced typing.
  const showSection =
    !state.loading &&
    (state.pending.length > 0 || state.accepted.length > 0) &&
    !collapsed;

  return (
    <>
      {editing ? (
        <EditClinicalTaskSheet
          task={editing}
          practitioners={state.practitioners}
          onSave={(edit) => state.applyEdit(editing.id, edit)}
          onAccept={() => state.decide(editing.id, 'approve')}
          onDelete={() => state.decide(editing.id, 'reject')}
          onClose={() => setEditing(null)}
        />
      ) : null}
      {showSection ? (
        <section aria-label="Clinical Tasks" data-testid="clinical-tasks-panel">
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2">
              <Icon name="List" size="lg" />
              <Heading size="xl" weight="semibold">
                Tasks
              </Heading>
              <Badge
                variant="secondary"
                className="bg-bg-indigo-subtle-primary text-text-indigo-subtle"
                data-testid="clinical-tasks-count"
              >
                {state.pending.length + state.accepted.length}
              </Badge>
            </div>
            <div className="flex w-full flex-col gap-4">
              {state.pending.length > 0 ? (
                <div
                  className="flex flex-col gap-3"
                  data-testid="clinical-tasks-pending"
                >
                  {state.pending.map((t) => (
                    <div
                      key={t.id}
                      className="flex flex-col gap-3 rounded-xl border border-border-primary bg-white p-4"
                      data-testid="clinical-task-pending-row"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <Badge
                          variant="secondary"
                          className="gap-1 bg-bg-indigo-subtle-primary text-text-indigo-subtle"
                        >
                          <Icon name={categoryIcon(t.category)} size="xs" />
                          {categoryLabel(t.category)}
                        </Badge>
                        <div className="flex items-center gap-1">
                          <Badge variant="secondary" className="font-medium">
                            Draft
                          </Badge>
                          <Button
                            size="sm"
                            variant="ghost"
                            aria-label="Reject"
                            disabled={state.mutating}
                            onClick={() => decide(t.id, 'reject')}
                          >
                            <Icon name="Trash2" size="sm" />
                          </Button>
                        </div>
                      </div>
                      <Text
                        size="md"
                        weight="semibold"
                        data-testid="clinical-task-description"
                      >
                        {t.description}
                      </Text>
                      {t.evidence ? (
                        <Text
                          size="xs"
                          color="tertiary"
                          className="line-clamp-2 italic"
                        >
                          source: “{t.evidence}”
                        </Text>
                      ) : null}
                      <div className="border-t border-border-secondary" />
                      <PriorityChip priority={t.priority} />
                      <div className="flex flex-col gap-2">
                        <DetailRow label="For" value={t.forDisplay} />
                        <DetailRow
                          label="Assigned to"
                          value={state.practitionerName(t.ownerReference)}
                        />
                        <DetailRow label="Planned" value={t.dueDate ?? '—'} />
                      </div>
                      <div className="flex gap-1 pt-1">
                        <Button
                          size="sm"
                          disabled={state.mutating}
                          onClick={() => decide(t.id, 'approve')}
                        >
                          Accept
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={state.mutating}
                          onClick={() => setEditing(t)}
                        >
                          Edit
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : null}

              {state.accepted.length > 0 ? (
                <div
                  className="flex flex-col gap-1 rounded-xl border border-border-primary bg-white p-4"
                  data-testid="clinical-tasks-accepted"
                >
                  <Text size="sm" weight="medium" color="tertiary">
                    Accepted
                  </Text>
                  <div className="flex flex-col divide-y divide-border-secondary">
                    {state.accepted.map((t) => (
                      <div
                        key={t.id}
                        className="flex items-center gap-3 py-2"
                        data-testid="clinical-task-accepted-row"
                      >
                        <Checkbox
                          aria-label={`Mark "${t.description}" as ${t.done ? 'to do' : 'done'}`}
                          checked={t.done}
                          disabled={state.mutating}
                          onCheckedChange={(checked) =>
                            void setDone(t, checked === true)
                          }
                        />
                        <div className="flex min-w-0 flex-1 flex-col">
                          <Text
                            weight="medium"
                            data-testid="clinical-task-description"
                            className={t.done ? 'line-through opacity-60' : ''}
                          >
                            {t.description}
                          </Text>
                          <Text size="xs" color="tertiary">
                            {[
                              t.done ? 'Done' : 'To do',
                              t.dueDate ? `Due ${t.dueDate}` : null,
                              state.practitionerName(t.ownerReference),
                            ]
                              .filter(Boolean)
                              .join(' · ')}
                          </Text>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </section>
      ) : null}
    </>
  );
};
